import type { StaffRole } from './constants';

/**
 * Fine-grained permissions. Roles are mapped onto permissions rather than being
 * checked directly, so access rules can be tightened later without touching
 * every route handler.
 */
export const PERMISSIONS = [
  'pos:operate', // open tables, take orders, send items
  'pos:cancel_item', // void an item that has already been sent
  'payments:process',
  'orders:discount', // put a percentage or fixed discount on a bill
  'print:station', // claim and print queued bills at the till
  'orders:history',
  'reservations:manage', // take, change, cancel and no-show bookings; waiters can still see and seat them
  'reports:read', // takings, best sellers and the rest of the management figures
  'kds:kitchen',
  'kds:bar',
  'admin:menu', // categories, items, modifiers
  'admin:tables',
  'admin:settings',
  'admin:users',
  'audit:read',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

const ALL: Permission[] = [...PERMISSIONS];

export const ROLE_PERMISSIONS: Record<StaffRole, readonly Permission[]> = {
  ADMIN: ALL,
  MANAGER: ALL.filter((p) => p !== 'admin:users'),
  WAITER: [
    'pos:operate',
    'pos:cancel_item',
    'payments:process',
    'print:station',
    'orders:history',
    'kds:kitchen',
    'kds:bar',
  ],
  KITCHEN: ['kds:kitchen'],
  BAR: ['kds:bar'],
};

export function can(role: StaffRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/** Landing route for a role right after login. */
export const ROLE_HOME: Record<StaffRole, string> = {
  ADMIN: '/pos',
  MANAGER: '/pos',
  WAITER: '/pos',
  KITCHEN: '/kitchen',
  BAR: '/bar',
};
